import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, Play, ArrowRight, Users, TrendingUp, CheckCircle, ChevronUp } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Footer } from "@/components/Footer";
import { MobileNavMenu } from "@/components/MobileNavMenu";
import { useTranslation } from "react-i18next";
import { soundManager } from "@/utils/sounds";
import {AutoTranslate} from "@/lib/autoTranslate.tsx"
import { getCalApi } from "@calcom/embed-react";
import { SEO } from "@/components/SEO";


const AutoTypewriter = () => {
  const { t, i18n } = useTranslation()
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const phrases = [
    "I'm ready, let's build my resume!",
    "Yayyy, let’s do this!!!",
    "I need a CV that will get me noticed",
    "I want a resume that will land me interviews",
  ];

  useEffect(() => {
    const currentPhrase = phrases[currentIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (currentText.length < currentPhrase.length) {
          setCurrentText(currentPhrase.slice(0, currentText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (currentText.length > 0) {
          setCurrentText(currentText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentIndex, phrases]);

  return (
    <div className="bg-card border border-input rounded-xl p-4 shadow-sm min-h-[60px] flex items-center">
      <span className="text-muted-foreground">
        {currentText}
        <span className="animate-pulse">|</span>
      </span>
    </div>
  );
};

const TestimonialCard = ({ name, role, content, rating = 5 }: {
  name: string;
  role: string;
  content: string;
  rating?: number;
}) => (
  <Card className="min-w-[300px] mx-2 bg-card border-card-border">
    <CardContent className="p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
          {name.charAt(0)}
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{name}</h3>
          <p className="text-sm text-muted-foreground">{role}</p>
          <div className="flex gap-1 mt-1">
            {Array.from({ length: rating }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
        </div>
      </div>
      <p className="text-muted-foreground">{content}</p>
    </CardContent>
  </Card>
);

const testimonials = [
  { name: "Sarah Johnson", role: "Marketing Manager", content: "FIND Resume Builder helped me land my dream job! The AI guidance was incredible." },
  { name: "Mike Chen", role: "Software Developer", content: "Best resume builder I've ever used. The ATS optimization really works." },
  { name: "Emily Davis", role: "Data Analyst", content: "Got 3x more interviews after using this tool. Highly recommend!" },
  { name: "James Wilson", role: "Sales Executive", content: "The conversation flow made building my resume actually enjoyable." },
  { name: "Lisa Thompson", role: "Project Manager", content: "Professional results without the professional price tag." },
  { name: "David Brown", role: "UX Designer", content: "Clean, modern resumes that really stand out to recruiters." },
  { name: "Anna Garcia", role: "Financial Analyst", content: "The AI suggestions were spot-on for my industry." },
  { name: "Tom Anderson", role: "HR Specialist", content: "As a recruiter, I can confirm these resumes get noticed." },
  { name: "Rachel Lee", role: "Content Writer", content: "Finally, a resume builder that understands creativity." },
  { name: "Kevin Martinez", role: "Operations Manager", content: "Streamlined my entire job search process." },
  { name: "Sophie Taylor", role: "Graphic Designer", content: "Beautiful layouts that showcase my skills perfectly." },
  { name: "Alex Kumar", role: "Product Manager", content: "Data-driven approach to resume building. Love it!" },
  { name: "Maria Rodriguez", role: "Teacher", content: "Even for education sector, this tool was perfect." },
  { name: "John Smith", role: "Engineer", content: "Technical roles need technical precision. This delivers." },
  { name: "Amanda White", role: "Consultant", content: "Professional quality that matches my consulting standards." },
  { name: "Ryan Johnson", role: "Account Manager", content: "Client-facing roles require polish. This tool provides it." },
  { name: "Jennifer Kim", role: "Research Scientist", content: "Academic formatting made simple and professional." },
  { name: "Mark Thompson", role: "Business Analyst", content: "Data tells the story, and this tool tells it well." },
  { name: "Grace Liu", role: "Social Media Manager", content: "Modern design aesthetic that appeals to creative industries." },
  { name: "Chris Walker", role: "Startup Founder", content: "Entrepreneur-friendly approach to showcasing achievements." }
];

export default function Landing() {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const [showBackToTop, setShowBackToTop] = useState(false);

  const handleGetStarted = () => {
    window.location.href = '/auth?tab=signup';
  };

  const handleSignIn = () => {
    soundManager.signIn();
    window.location.href = '/auth?tab=signin';
  };

  const handleDemo = () => {
    // Trigger Cal.com scheduling modal
    const button = document.querySelector('[data-cal-namespace="demo"]') as HTMLButtonElement;
    if (button) {
      button.click();
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({"namespace":"demo"});
      cal("ui", {"theme":"light","cssVarsPerTheme":{"light":{"cal-brand":"#ff339c"},"dark":{"cal-brand":"#ff339c"}},"hideEventTypeDetails":false,"layout":"month_view"});
    })();
  }, []);

  return (
    <main className="min-h-screen bg-gradient-main">
      <SEO
        title="FIND Resume Builder — Build the perfect AI resume"
        description="Create ATS-friendly resumes in minutes with FIND's AI Resume Builder. Tailor your CV to any job, export to PDF or DOCX, and land more interviews."
        path="/"
      />
      {/* Background Pattern */}
      <div
        className="fixed inset-0 opacity-30 pointer-events-none dark:opacity-10"
        style={{
          backgroundImage: `var(--grid-pattern)`,
        }}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between p-4 md:p-6 border-b border-border bg-card/60 backdrop-blur-md">
        <div className="flex items-center gap-8">
          <a href="/" className="text-xl md:text-2xl font-bold text-primary hover:opacity-80 transition-opacity">
            {AutoTranslate(`Resume Builder`)}
          </a>
          <nav className="hidden md:flex items-center gap-6">
            <Button variant="ghost" onClick={handleDemo}>
              <Play className="h-4 w-4 mr-2" />
              {AutoTranslate(`Demo`)}
            </Button>
            <Button
              variant="ghost"
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {AutoTranslate(`How It Works`)}
            </Button>
            <Button variant="ghost" onClick={() => window.location.href = '/pricing'}>
              {AutoTranslate(`Pricing`)}
            </Button>
          </nav>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <div id="google_translate_element" className="hidden"></div>
          <LanguageSelector />
          <ThemeToggle />
          <div className="hidden md:flex items-center gap-4">
            <Button variant="ghost" onClick={handleSignIn}>
              {t('signIn')}
            </Button>
            <Button onClick={handleGetStarted}>
              {t('landing.getStarted')}
            </Button>
          </div>
          <MobileNavMenu
            onDemo={handleDemo}
            onPricing={() => window.location.href = '/pricing'}
            onSignIn={handleSignIn}
            onGetStarted={handleGetStarted}
          />
        </div>
      </header>

      {/* Google Translate Script */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            function googleTranslateElementInit() {
              new google.translate.TranslateElement(
                {pageLanguage: 'en'},
                'google_translate_element'
              );
            }
          `
        }}
      />
      <script
        type="text/javascript"
        src="https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"
      />

      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-12 px-4">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-4 md:mb-6">
              {AutoTranslate(`Build Your Resume with AI,`)} {" "}
              <span className="text-primary">{AutoTranslate(`Impress Recruiters`)}</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-6 md:mb-8 max-w-2xl mx-auto">
              {AutoTranslate(`Easy as ABC, Fast as 1-2-3`)}
            </p>

            {/* Animated Chat Box */}
            <div className="max-w-md mx-auto mb-6 md:mb-8">
              <AutoTypewriter />
            </div>

            <div className="flex flex-col items-center gap-3 md:gap-4 mb-8 md:mb-12">
              <Button size="lg" onClick={handleGetStarted} className="text-base md:text-lg px-6 md:px-8 py-3 md:py-4">
                {AutoTranslate(`Get Started`)}
                <ArrowRight className="h-4 md:h-5 w-4 md:w-5 ml-2" />
              </Button>
              <p className="text-xs md:text-sm text-muted-foreground">{AutoTranslate(`Start free, upgrade when you're ready. No credit card required.`)}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary mb-2">75%</div>
                <div className="text-sm md:text-base text-muted-foreground">{AutoTranslate(`of resumes are rejected by ATS before a human sees them`)}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary mb-2">63%</div>
                <div className="text-sm md:text-base text-muted-foreground">{AutoTranslate(`of recruiters prefer tailored resumes for the job`)}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary mb-2">3x</div>
                <div className="text-sm md:text-base text-muted-foreground">{AutoTranslate(`more interview callbacks with an ATS-optimized resume`)}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 md:mb-6">
            {AutoTranslate(`How It Works`)}
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mb-8 md:mb-10 max-w-2xl mx-auto">
            {AutoTranslate(`Watch a quick walkthrough of how to build your resume in minutes.`)}
          </p>

          <div className="relative max-w-4xl mx-auto">
            {/* Subtle background shadow behind the video */}
            <div
              aria-hidden="true"
              className="absolute -inset-6 md:-inset-10 rounded-[2rem] bg-primary/10 blur-3xl pointer-events-none"
            />

            {/* Mac-style window with themed top border */}
            <div className="relative rounded-2xl overflow-hidden border border-card-border bg-card shadow-2xl">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-card-border bg-foreground/95 dark:bg-background">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" />
              </div>
              <video
                src="/how-it-works.mp4"
                controls
                playsInline
                preload="metadata"
                className="w-full h-auto block bg-background"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 md:mb-6">
            {AutoTranslate(`Build an ATS friendly resume from scratch in just minutes`)}
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground mb-6 md:mb-8 max-w-3xl mx-auto px-4">
            {AutoTranslate(`Even if you're a beginner. We'll walk you through each step with just a simple conversation to create a resume that stands out.`)}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12">
            <Card className="p-4 md:p-6 bg-card border-card-border">
              <Users className="h-10 md:h-12 w-10 md:w-12 text-primary mx-auto mb-3 md:mb-4" />
              <h3 className="text-lg md:text-xl font-semibold mb-2">{AutoTranslate(`AI-Guided Process`)}</h3>
              <p className="text-sm md:text-base text-muted-foreground">{AutoTranslate(`Conversational interface that guides you step by step`)}</p>
            </Card>
            <Card className="p-4 md:p-6 bg-card border-card-border">
              <TrendingUp className="h-10 md:h-12 w-10 md:w-12 text-primary mx-auto mb-3 md:mb-4" />
              <h3 className="text-lg md:text-xl font-semibold mb-2">{AutoTranslate(`ATS Optimized`)}</h3>
              <p className="text-sm md:text-base text-muted-foreground">{AutoTranslate(`Built to pass Applicant Tracking Systems`)}</p>
            </Card>
            <Card className="p-4 md:p-6 bg-card border-card-border">
              <CheckCircle className="h-10 md:h-12 w-10 md:w-12 text-primary mx-auto mb-3 md:mb-4" />
              <h3 className="text-lg md:text-xl font-semibold mb-2">{AutoTranslate(`Professional Quality`)}</h3>
              <p className="text-sm md:text-base text-muted-foreground">{AutoTranslate(`Industry-standard formatting and design`)}</p>
            </Card>
          </div>

          <Button size="lg" onClick={handleDemo} className="text-base md:text-lg px-6 md:px-8 py-3 md:py-4">
            <Play className="h-4 md:h-5 w-4 md:w-5 mr-2" />
            {AutoTranslate(`Schedule Demo`)}
          </Button>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-8 md:mb-12 px-4">
            {AutoTranslate(`What Others Are Saying`)}
          </h2>

          {/* First row - moving right */}
          <div className="overflow-hidden mb-8">
            <div className="flex gap-8 animate-scroll-right hover:pause-animation"
                 onMouseEnter={(e) => e.currentTarget.style.animationPlayState = 'paused'}
                 onMouseLeave={(e) => e.currentTarget.style.animationPlayState = 'running'}>
              {testimonials.slice(0, 10).map((testimonial, index) => (
                <TestimonialCard key={index} {...testimonial} />
              ))}
              {testimonials.slice(0, 10).map((testimonial, index) => (
                <TestimonialCard key={`duplicate-${index}`} {...testimonial} />
              ))}
            </div>
          </div>

          {/* Second row - moving left */}
          <div className="overflow-hidden">
            <div className="flex gap-8 animate-scroll-left hover:pause-animation"
                 onMouseEnter={(e) => e.currentTarget.style.animationPlayState = 'paused'}
                 onMouseLeave={(e) => e.currentTarget.style.animationPlayState = 'running'}>
              {testimonials.slice(10, 20).map((testimonial, index) => (
                <TestimonialCard key={index} {...testimonial} />
              ))}
              {testimonials.slice(10, 20).map((testimonial, index) => (
                <TestimonialCard key={`duplicate-${index}`} {...testimonial} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-16 md:py-20 px-4 md:px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 md:mb-8 px-4">
            {AutoTranslate(`Build Resumes That Leave a Good First Impression`)}
          </h2>
          <Button size="lg" onClick={handleGetStarted} className="text-base md:text-lg px-6 md:px-8 py-3 md:py-4">
            <ArrowRight className="h-4 md:h-5 w-4 md:w-5 mr-2" />
            {AutoTranslate(`Start Building`)}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Cal.com Embed Button (Hidden) */}
      <button 
        data-cal-namespace="demo"
        data-cal-link="f-i-n-d/demo"
        data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true","theme":"light"}'
        style={{ display: 'none' }}
      />

      {/* Back to Top Button */}
      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 rounded-full w-12 h-12 p-0 bg-pink-500 hover:bg-pink-600 text-white shadow-lg"
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </Button>
      )}
    </main>
  );
}